import { UrlMatchResult, UrlSegment } from '@angular/router';
import { isReservedFrontUrlSegment } from '@constants/reserved-front-urls';
import { ROUTE_PATHS } from '@constants/routes';
import { environment } from '@environments/environment';
import { extractWhiteLabelConfig } from '@utils/common.utils';

export const SUB_PAGE_VIEW_CONSTANTS = {
  ROUTE_PARAMS: {
    USERNAME: 'id',
    SUB_PAGE_URL: 'subPageUrl'
  },
  QUERY_PARAMS: {
    PREVIEW: 'preview'
  },
  QUERY_VALUES: {
    TRUE: 'true'
  },
  MESSAGES: {
    NOT_FOUND: 'Sub-page not found'
  },
  RESERVED_ROOT_SEGMENTS: [
    ROUTE_PATHS.AUTH,
    ROUTE_PATHS.DASHBOARD,
    ROUTE_PATHS.EDIT_PREFERENCE,
    ROUTE_PATHS.FORUM,
    ROUTE_PATHS.MONETIZE,
    ROUTE_PATHS.WALLET,
    ROUTE_PATHS.MESSAGES,
    ROUTE_PATHS.INSIGHTS,
    ROUTE_PATHS.PRIVACY_POLICY,
    ROUTE_PATHS.TERMS_CONDITIONS,
    ROUTE_PATHS.CONTACT_US,
    ROUTE_PATHS.ABOUT,
    ROUTE_PATHS.PRICING,
    ROUTE_PATHS.ADVERTISE,
    ROUTE_PATHS.TERMS_OF_SERVICE,
    ROUTE_PATHS.HELP_FAQ,
    ROUTE_PATHS.ACCESS_USER,
    ROUTE_PATHS.NOT_FOUND,
    ROUTE_PATHS.UNSUBSCRIBE_INSTRUCTION,
    ROUTE_PATHS.BIO,
    ROUTE_PATHS.CREATOR,
    ROUTE_PATHS.CREATORS,
    ROUTE_PATHS.POST,
    ROUTE_PATHS.GLOBAL,
    ROUTE_PATHS.CONTENT,
    ROUTE_PATHS.REQUEST_DEMO,
    ROUTE_PATHS.SCHEDULE_A_DEMO,
    ROUTE_PATHS.RESOURCES,
    ROUTE_PATHS.SHOPIFY_CONNECT,
    ROUTE_PATHS.CALENDAR_CONNECT,
    ROUTE_PATHS.ORDER_STATUS,
    ROUTE_PATHS.ADVERTISEMENT_HOME,
    ROUTE_PATHS.EXPLORE_CREATORS_ROUTE,
    'home',
    'wls',
    'share',
    'unsubscribe',
    'imported',
    'opt-out'
  ]
} as const;

function getWhiteLabelOwnerUsername(): string {
  const username = extractWhiteLabelConfig()?.user?.user_name;
  return typeof username === 'string' ? username.trim() : '';
}

export function whiteLabelRootSubPageMatcher(
  segments: UrlSegment[]
): UrlMatchResult | null {
  if (!environment.whiteLabelConfig || segments.length !== 1) {
    return null;
  }

  const slug = segments[0]?.path?.trim() ?? '';
  if (!slug) {
    return null;
  }

  const ownerUsername = getWhiteLabelOwnerUsername();
  if (isReservedFrontUrlSegment(slug, ownerUsername ? [ownerUsername] : [])) {
    return null;
  }

  return {
    consumed: segments,
    posParams: {
      [SUB_PAGE_VIEW_CONSTANTS.ROUTE_PARAMS.SUB_PAGE_URL]: segments[0]
    }
  };
}

export function isPublicSubPagePath(urlPath: string): boolean {
  const path = (urlPath || '').split('?')[0].split('#')[0];
  const segments = path.split('/').filter(Boolean);
  const reservedRoots = SUB_PAGE_VIEW_CONSTANTS.RESERVED_ROOT_SEGMENTS as readonly string[];
  const ownerUsername = getWhiteLabelOwnerUsername().toLowerCase();

  if (segments.length === 1) {
    if (!environment.whiteLabelConfig) {
      return false;
    }
    const slug = segments[0];
    if (isReservedFrontUrlSegment(slug, ownerUsername ? [ownerUsername] : [])) {
      return false;
    }
    return true;
  }

  if (segments.length === 2) {
    const firstSegment = segments[0];
    if (reservedRoots.includes(firstSegment)) {
      return false;
    }
    if (isReservedFrontUrlSegment(firstSegment, ownerUsername ? [ownerUsername] : [])) {
      return false;
    }
    return !!segments[1];
  }

  return false;
}

export function isCoverPageShellPath(urlPath: string): boolean {
  const path = (urlPath || '').split('?')[0].split('#')[0];
  if (isPublicSubPagePath(path)) {
    return true;
  }
  const isPublishedWhiteLabelHome =
    !!environment.whiteLabelConfig &&
    environment.is_whitelabel_cover_page_published === true &&
    (path === ROUTE_PATHS.HOME_PATH || path === '');
  return isPublishedWhiteLabelHome;
}
